import React from 'react';
import { Link } from 'react-router-dom';
import './module.css';

// The depth tier of a /platform/:slug page. Every component here renders one
// section of a module's `depth` object (frontend/src/data/modules.js) and
// nothing else — the data decides what appears, the template decides the
// order. Each section carries a stable id so NoVA's scroll_page and the
// pitch can land on it. Product facts (counts, limits, defaults, prices)
// are expected; performance numbers come only from measurements.gen.json
// ([[website-product-first-messaging]] — the three depth rules).

// Hero chips: three or four facts a buyer sizes the product by.
export function FactChips({ facts }) {
  if (!facts?.length) return null;
  return (
    <ul className="mdp-facts" aria-label="At a glance">
      {facts.map((f) => (
        <li key={f.k}>
          <strong>{f.v}</strong>
          <span>{f.k}</span>
        </li>
      ))}
    </ul>
  );
}

// One change end to end: numbered steps, each with the screen it happens on
// when a capture exists (`shot`), otherwise the step alone.
export function ScreenLoop({ steps, title = 'In 90 seconds', intro }) {
  if (!steps?.length) return null;
  return (
    <section className="cs-store-section mdp-section" id="loop">
      <h2>{title}</h2>
      {intro && <p className="cs-store-note mdp-intro">{intro}</p>}
      <ol className="mdp-loop">
        {steps.map((s, i) => (
          <li key={s.name} className={`cs-sw-card mdp-loop-step${s.shot ? ' has-shot' : ''}${s.wide ? ' wide' : ''}`}>
            <div className="mdp-loop-copy">
              <span className="mdp-n">{String(i + 1).padStart(2, '0')}</span>
              <h3>{s.name}</h3>
              <p>{s.body}</p>
              {s.view && <small className="mdp-where">{s.view}</small>}
            </div>
            {s.shot && (
              <figure className="mdp-shot">
                <img src={s.shot} alt={s.cap || s.name} loading="lazy" />
                {s.cap && <figcaption>{s.cap}</figcaption>}
              </figure>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

// What you can do: groups of capabilities, each item a name, two sentences,
// the view it lives in and the plan it comes with.
export function CapabilityGrid({ groups, title = 'What you can do', intro }) {
  if (!groups?.length) return null;
  return (
    <section className="cs-store-section mdp-section" id="capabilities">
      <h2>{title}</h2>
      {intro && <p className="cs-store-note mdp-intro">{intro}</p>}
      {groups.map((g) => (
        <div key={g.group} className="mdp-cap-group" id={g.id}>
          <div className="mdp-cap-head">
            <h3>{g.group}</h3>
            {g.intro && <p>{g.intro}</p>}
          </div>
          <ul className="mdp-cap-items">
            {g.items.map((it) => (
              <li key={it.name} className="cs-sw-card mdp-cap">
                <h4>{it.name}</h4>
                <p>{it.body}</p>
                {(it.view || it.plan) && (
                  <div className="mdp-cap-meta">
                    {it.view && <span className="mdp-tag">{it.view}</span>}
                    {it.plan && <span className={`mdp-tag mdp-tag-plan${/^(in progress|ask us|preview)$/.test(it.plan) ? ' is-soft' : ''}`}>{it.plan}</span>}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

// Three ways to use it: one card per arrival mode with the same rows, so
// the modes read side by side. A mode may carry `sub` variants (Keep's two
// runtimes) that repeat the rows that differ.
export function ModesGrid({ modes, title = 'Three ways to use it', intro, foot }) {
  if (!modes?.cols?.length) return null;
  return (
    <section className="cs-store-section mdp-section" id="modes">
      <h2>{modes.title || title}</h2>
      {(modes.intro || intro) && <p className="cs-store-note mdp-intro">{modes.intro || intro}</p>}
      <ol className="mdp-modes">
        {modes.cols.map((m, i) => (
          <li key={m.id} className="cs-sw-card mdp-mode" id={m.id}>
            <span className="mdp-n">{i + 1}</span>
            <h3>{m.name}</h3>
            {m.who && <p className="mdp-mode-who">{m.who}</p>}
            <dl className="mdp-mode-rows">
              {m.rows.map((r) => (
                <div key={r.k}><dt>{r.k}</dt><dd>{r.v}</dd></div>
              ))}
            </dl>
            {m.sub?.map((v) => (
              <div key={v.name} className="mdp-mode-sub">
                <h4>{v.name}</h4>
                <dl className="mdp-mode-rows">
                  {v.rows.map((r) => (
                    <div key={r.k}><dt>{r.k}</dt><dd>{r.v}</dd></div>
                  ))}
                </dl>
              </div>
            ))}
            {m.note && <p className="mdp-mode-note">{m.note}</p>}
          </li>
        ))}
      </ol>
      {(modes.foot || foot) && <p className="cs-land-micro">{modes.foot || foot}</p>}
    </section>
  );
}

// The trust ladder: how far the console goes on its own, as three rungs.
export function TrustLadder({ rungs, title = 'How far it goes on its own', intro, note, shot }) {
  if (!rungs?.length) return null;
  return (
    <section className="cs-store-section mdp-section" id="ladder">
      <h2>{title}</h2>
      {intro && <p className="cs-store-note mdp-intro">{intro}</p>}
      {shot && (
        <figure className="mdp-shot mdp-shot-wide">
          <img src={shot.src} alt={shot.alt} loading="lazy" />
          {shot.cap && <figcaption>{shot.cap}</figcaption>}
        </figure>
      )}
      <ol className="mdp-ladder">
        {rungs.map((r, i) => (
          <li key={r.name} className={`cs-sw-card mdp-rung${r.isDefault ? ' is-default' : ''}`}>
            <span className="mdp-n">{i + 1}</span>
            <h3>{r.name}{r.isDefault && <em>default</em>}</h3>
            <p className="mdp-rung-line">{r.line}</p>
            <ul>
              {r.does.map((d) => <li key={d}>{d}</li>)}
            </ul>
          </li>
        ))}
      </ol>
      {note && <p className="cs-land-micro">{note}</p>}
    </section>
  );
}

// A week with it: a day-in-the-life, always labelled synthetic.
export function WeekWith({ days, title = 'A week with it', intro }) {
  if (!days?.length) return null;
  return (
    <section className="cs-store-section mdp-section" id="week">
      <h2>{title}</h2>
      <p className="cs-land-micro mdp-synthetic">Synthetic week. Not a customer; the screens and settings are real.</p>
      {intro && <p className="cs-store-note mdp-intro">{intro}</p>}
      <ol className="mdp-week">
        {days.map((d) => (
          <li key={d.day} className="cs-sw-card mdp-day">
            <strong>{d.day}</strong>
            <p>{d.body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

// What it costs at your volume: worked examples whose dollars come from
// plans.js at the call site, never typed here.
export function CostExamples({ rows, title = 'What it costs at your volume', intro, note }) {
  if (!rows?.length) return null;
  return (
    <section className="cs-store-section mdp-section" id="cost">
      <h2>{title}</h2>
      {intro && <p className="cs-store-note mdp-intro">{intro}</p>}
      <div className="mdp-cost">
        {rows.map((r) => (
          <div key={r.scenario} className="cs-sw-card mdp-cost-row">
            <h3>{r.scenario}</h3>
            <p className="mdp-cost-math">{r.math}</p>
            <div className="mdp-cost-result">{r.result}</div>
          </div>
        ))}
      </div>
      {note && <p className="cs-land-micro">{note}</p>}
      <p className="cs-land-micro"><Link to="/pricing">Full pricing →</Link></p>
    </section>
  );
}

// Limits and defaults: facts from the live-config defaults, with where each
// is set so a buyer can see it is a setting and not a ceiling.
export function LimitsTable({ rows, title = 'Limits and defaults', intro }) {
  if (!rows?.length) return null;
  return (
    <section className="cs-store-section mdp-section" id="limits">
      <h2>{title}</h2>
      {intro && <p className="cs-store-note mdp-intro">{intro}</p>}
      <div className="cs-plan-tablewrap cs-land-compare mdp-limits">
        <table>
          <thead>
            <tr><th>What</th><th>Default</th><th>Where it is set</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.k}>
                <td><strong>{r.k}</strong></td>
                <td className="cs-land-us">{r.v}</td>
                <td>{r.src}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// What you need to start.
export function NeedsList({ items, title = 'What you need', intro }) {
  if (!items?.length) return null;
  return (
    <section className="cs-store-section mdp-section" id="needs">
      <h2>{title}</h2>
      {intro && <p className="cs-store-note mdp-intro">{intro}</p>}
      <ul className="mdp-needs">
        {items.map((n) => <li key={n} className="cs-sw-card">{n}</li>)}
      </ul>
    </section>
  );
}

// Compared with the usual way: mechanism against mechanism, no vendor names
// unless the owner approves them.
export function CompareRows({ rows, title = 'Compared with the usual way', intro }) {
  if (!rows?.length) return null;
  return (
    <section className="cs-store-section mdp-section" id="compare">
      <h2>{title}</h2>
      {intro && <p className="cs-store-note mdp-intro">{intro}</p>}
      <div className="cs-plan-tablewrap cs-land-compare mdp-compare">
        <table>
          <thead>
            <tr><th>The usual way</th><th>Here</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.usual}>
                <td>{r.usual}</td>
                <td className="cs-land-us">{r.here}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// Measured: only values read from measurements.gen.json, source on screen.
export function MeasuredStrip({ items, title = 'Measured', intro }) {
  if (!items?.length) return null;
  return (
    <section className="cs-store-section mdp-section" id="measured">
      <h2>{title}</h2>
      {intro && <p className="cs-store-note mdp-intro">{intro}</p>}
      <ul className="mdp-measured">
        {items.map((m) => (
          <li key={m.k} className="cs-sw-card">
            <strong>{m.v}</strong>
            <span>{m.k}</span>
            {m.d && <p>{m.d}</p>}
            <small>{m.src}</small>
          </li>
        ))}
      </ul>
    </section>
  );
}

// FAQ: absorbs "what it is not", preview status and plan availability.
export function Faq({ items, title = 'Questions people ask' }) {
  if (!items?.length) return null;
  return (
    <section className="cs-store-section mdp-section" id="faq">
      <h2>{title}</h2>
      <div className="mdp-faq">
        {items.map((f) => (
          <details key={f.q} className="cs-sw-card mdp-faq-item">
            <summary>{f.q}</summary>
            <p>{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

// Plan availability: which rungs include the module, read from the
// module's structured `plans` field so /pricing and the page agree.
export function PlanRow({ plans }) {
  if (!plans?.length) return null;
  return (
    <ul className="mdp-plans" aria-label="Plans">
      {plans.map((p) => (
        <li key={p.name} className={p.included ? 'is-in' : 'is-out'}>
          <span>{p.name}</span>
          <small>{p.note}</small>
        </li>
      ))}
    </ul>
  );
}
